import type { CurationQueueSource } from '@avisoma/shared';

/**
 * The decision logic behind a grouped curation verdict (DR-3318), kept pure and apart
 * from the mutation so it can be tested without React, react-query, or the network.
 *
 * Two decisions live here:
 *  1. what each page in the group is actually submitted as (`planSubmissions`), and
 *  2. what the results mean for the pool, the session stats, and the toast
 *     (`resolveVerdictOutcomes`).
 */

export type Verdict = 'confirmed' | 'corrected';

/** A page as the curator sees it — the anchor under review, or a selected similar page. */
export type VerdictPage = {
  pageId: string;
  currentCategory: string | null;
  source: CurationQueueSource;
};

/** One page's POST: the verdict actually recorded for it, and its own stale-guard. */
export type VerdictSubmission = {
  pageId: string;
  /** The category the curator saw on THAT page. The server 409s if it moved on. */
  expectedCategory: string | null;
  source: CurationQueueSource;
  isAnchor: boolean;
  verdict: Verdict;
  /** Only sent for 'corrected'. null means "unset the page's category". */
  newCategory: string | null;
};

export type SubmissionOutcome = {
  submission: VerdictSubmission;
  ok: boolean;
  /** HTTP status of the failure. 409 = the page's category moved on. */
  status?: number;
};

export type VerdictToast = { kind: 'success' | 'error' | 'info' | 'none'; message: string };

export type VerdictResolution = {
  /** Pages that leave the pool now — recorded, or gone stale under the curator. */
  leaving: string[];
  /** Verdicts actually recorded (stats.reviewed / stats.streak). */
  reviewed: number;
  /** How many of those were corrections (stats.corrected). */
  corrected: number;
  /** Categories whose training set moved, so the retrain suggestion names them. */
  touchedCategories: string[];
  /**
   * Members whose POST did not land. They stay selected AND their cached category is now
   * suspect — a 409 says the page moved on, so retrying against the same cached
   * `currentCategory` would 409 forever. Their presence is what forces a cohort refetch.
   */
  failedMemberIds: string[];
  /**
   * Members that 409'd. Their cached `currentCategory` is known-wrong. When the view
   * advances past them they must be evicted from the queue cache and refetched, or
   * they resurface stale and 409 again — then the solo path skips them for the session.
   */
  staleMemberIds: string[];
  /** Anchor recorded or skipped — the view advances off this group. */
  anchorLeft: boolean;
  toast: VerdictToast;
};

/** A blank category name and "no category at all" are the same thing (server: emptyToNull). */
const normalize = (category: string | null | undefined): string | null => category || null;

/**
 * The category EVERY page in the group ends at. A group ruling means "these pages all
 * belong here": on a correction that is the picked category (null = unset), on a confirm
 * it is the anchor's own category.
 */
export function targetCategory(
  anchorCategory: string | null,
  verdict: Verdict,
  newCategory: string | null | undefined,
): string | null {
  return verdict === 'corrected' ? normalize(newCategory) : normalize(anchorCategory);
}

/**
 * The members a verdict would actually land on — the group as the curator should see it.
 *
 * Confirming an UNCATEGORIZED anchor asserts "this page has no category". Fanning that
 * onto a labelled member would strip its label, which the curator never asked for, so
 * only members that are themselves uncategorized take part. Picking "no category"
 * explicitly (verdict 'corrected', target null) IS a deliberate unset and still reaches
 * every selected member.
 */
export function participatingMembers(
  anchor: VerdictPage,
  verdict: Verdict,
  newCategory: string | null | undefined,
  members: VerdictPage[],
): VerdictPage[] {
  const target = targetCategory(anchor.currentCategory, verdict, newCategory);
  return verdict === 'confirmed' && target === null
    ? members.filter((member) => normalize(member.currentCategory) === null)
    : members;
}

/**
 * The members still covered by a selection snapshot taken earlier — used by the category
 * picker, which is a second screen: the curator chose "wrong" against the group they could
 * see, and a cohort that arrives (or recovers from an error) while the dialog is open must
 * not join that ruling. Members deselected or evicted meanwhile drop out on their own.
 *
 * A null snapshot means "not armed" and restricts nothing. An EMPTY one is a real
 * restriction — solo at open stays solo, whatever the cohort does afterwards.
 */
export function membersInSnapshot<T extends { pageId: string }>(
  members: T[],
  snapshot: ReadonlySet<string> | null,
): T[] {
  return snapshot ? members.filter((member) => snapshot.has(member.pageId)) : members;
}

/** One UUID for a multi-page ruling; omitted on a solo review so history stays ungrouped. */
export function groupSubmissionBatchId(submissionCount: number): string | undefined {
  return submissionCount > 1 ? crypto.randomUUID() : undefined;
}

/** Pages one verdict lands on: the anchor plus the members it actually touches. */
export function participatingCount(
  anchor: VerdictPage,
  verdict: Verdict,
  newCategory: string | null | undefined,
  members: VerdictPage[],
): number {
  return 1 + participatingMembers(anchor, verdict, newCategory, members).length;
}

/**
 * One submission per participating page. The ANCHOR records exactly the verdict the
 * curator clicked, so a solo review is byte-identical to what it was before grouping
 * existed.
 *
 * A MEMBER is judged against the target, not against the anchor's click: a member that
 * already carries the target category is a 'confirmed' (the server rejects a no-op
 * correction anyway), and a member carrying anything else is 'corrected' onto the target
 * — confirming a member's own, different label would record wrong training data.
 */
export function planSubmissions(input: {
  anchor: VerdictPage;
  verdict: Verdict;
  newCategory?: string | null;
  members: VerdictPage[];
}): VerdictSubmission[] {
  const target = targetCategory(input.anchor.currentCategory, input.verdict, input.newCategory);
  const participating = participatingMembers(
    input.anchor,
    input.verdict,
    input.newCategory,
    input.members,
  );

  return [
    {
      pageId: input.anchor.pageId,
      expectedCategory: input.anchor.currentCategory,
      source: input.anchor.source,
      isAnchor: true,
      verdict: input.verdict,
      newCategory: target,
    },
    ...participating.map((member) => ({
      pageId: member.pageId,
      expectedCategory: member.currentCategory,
      source: member.source,
      isAnchor: false,
      verdict: (normalize(member.currentCategory) === target ? 'confirmed' : 'corrected') as Verdict,
      newCategory: target,
    })),
  ];
}

/**
 * What the settled POSTs mean for the session.
 *
 * A 409 on the ANCHOR means the page moved on under the curator, so it leaves the pool as
 * a silent skip — in a group exactly as in a solo review, otherwise the same anchor comes
 * straight back and wedges the queue. A MEMBER that failed (409 included) stays selected
 * so the curator can retry it: nothing disappears from a group without a recorded verdict.
 */
export function resolveVerdictOutcomes(
  outcomes: SubmissionOutcome[],
  target: string | null,
): VerdictResolution {
  const solo = outcomes.length === 1;
  const succeeded = outcomes.filter((outcome) => outcome.ok);
  const anchor = outcomes.find((outcome) => outcome.submission.isAnchor);
  const anchorStale = !!anchor && !anchor.ok && anchor.status === 409;

  const leaving = [
    ...succeeded.map((outcome) => outcome.submission.pageId),
    ...(anchorStale ? [anchor.submission.pageId] : []),
  ];

  // A stale anchor is a skip, not a failure — it must not be reported as "kept selected".
  const failed = outcomes.filter(
    (outcome) => !outcome.ok && !(outcome.submission.isAnchor && outcome.status === 409),
  );
  const failedMemberIds = outcomes
    .filter((outcome) => !outcome.ok && !outcome.submission.isAnchor)
    .map((outcome) => outcome.submission.pageId);
  const staleMemberIds = outcomes
    .filter((outcome) => !outcome.ok && !outcome.submission.isAnchor && outcome.status === 409)
    .map((outcome) => outcome.submission.pageId);
  const anchorLeft = !!anchor && (anchor.ok || anchorStale);

  // Only a LANDED correction moved a training set. A failed one changed nothing, and a
  // confirm re-affirms the label the page already had.
  const correctedOk = succeeded.filter((outcome) => outcome.submission.verdict === 'corrected');
  const touched = new Set<string>();
  for (const outcome of correctedOk) {
    if (outcome.submission.expectedCategory) touched.add(outcome.submission.expectedCategory);
  }
  if (correctedOk.length > 0 && target) touched.add(target);

  return {
    leaving,
    reviewed: succeeded.length,
    corrected: correctedOk.length,
    touchedCategories: [...touched],
    failedMemberIds,
    staleMemberIds,
    anchorLeft,
    toast: solo
      ? soloToast(outcomes[0])
      : groupToast({
          succeeded: succeeded.length,
          failed: failed.length,
          anchorStale,
          // Anchor recorded or skipped = the view advances to the next page, so the failed
          // members are no longer on screen to stay "selected".
          anchorLeft,
        }),
  };
}

/** Drop pages whose cached category is known-wrong so they cannot resurface stale. */
export function evictStaleQueueMembers<T extends { pageId: string }>(
  items: T[],
  staleMemberIds: readonly string[],
): T[] {
  if (staleMemberIds.length === 0) return items;
  const stale = new Set(staleMemberIds);
  return items.filter((item) => !stale.has(item.pageId));
}

export type QueueRefreshPlan = {
  /** Drop these pageIds from every cached queue list immediately. */
  evictIds: string[];
  /** Force an active queue refetch so evicted pages can return with a fresh category. */
  refetch: boolean;
};

/**
 * A 409 member's cached queue row is known-wrong. Evict it even on same-anchor retry
 * so a later skip cannot resurface the stale category. Only refetch the queue once the
 * view has left — an in-place refetch would disrupt the strip the curator is retrying.
 */
export function planQueueRefresh(
  resolution: Pick<VerdictResolution, 'staleMemberIds' | 'anchorLeft'>,
): QueueRefreshPlan {
  if (resolution.staleMemberIds.length === 0) {
    return { evictIds: [], refetch: false };
  }
  return { evictIds: resolution.staleMemberIds, refetch: resolution.anchorLeft };
}

/**
 * Evicting the last pending 409 member can empty the list before the queue refetch
 * returns. Hold loading only for that refresh, not for a later background refetch
 * of an already-empty session.
 */
export function queueViewIsLoading(queue: {
  isLoading: boolean;
  awaitingRefresh: boolean;
  hasCurrent: boolean;
}): boolean {
  return queue.isLoading || (queue.awaitingRefresh && !queue.hasCurrent);
}

function soloToast(outcome: SubmissionOutcome): VerdictToast {
  if (outcome.ok) return { kind: 'none', message: '' };
  return outcome.status === 409
    ? { kind: 'error', message: 'Page category changed — skipping' }
    : { kind: 'error', message: 'Failed to save review — please try again' };
}

function groupToast(group: {
  succeeded: number;
  failed: number;
  anchorStale: boolean;
  anchorLeft: boolean;
}): VerdictToast {
  const { succeeded, failed, anchorStale, anchorLeft } = group;
  const skipped = anchorStale ? ' · this page changed — skipped' : '';
  if (failed > 0) {
    // Once the anchor leaves, the next page is on screen and the failed members are not.
    // They keep their open flag, so the queue serves them again — say that instead.
    const fate = anchorLeft ? 'still in the queue' : 'kept selected';
    return { kind: 'error', message: `${succeeded} applied, ${failed} failed — ${fate}${skipped}` };
  }
  if (anchorStale) {
    return { kind: 'info', message: `Applied to ${succeeded} pages${skipped}` };
  }
  return { kind: 'success', message: `Applied to ${succeeded} pages` };
}
